import { Link, useRouterState } from "@tanstack/react-router";
import { ChevronRight, Home } from "lucide-react";
import { SITE_ORIGIN } from "@/lib/seo";


/**
 * Friendly labels for known top-level segments. Anything not listed falls
 * back to a title-cased version of the URL segment.
 */
const SEGMENT_LABELS: Record<string, string> = {
  about: "About",
  accommodations: "Accommodations",
  auth: "Sign In",
  booking: "Booking",
  contact: "Contact",
  faq: "FAQ",
  "fish-species": "Fish Species",
  "fishing-report": "Fishing Report",
  gallery: "Gallery",
  links: "Helpful Links",
  privacy: "Privacy",
  reviews: "Reviews",
  shop: "Shop",
  "submit-photo": "Submit a Photo",
  terms: "Terms",
  weather: "Weather",
  admin: "Admin",
};

function labelForSegment(segment: string): string {
  if (SEGMENT_LABELS[segment]) return SEGMENT_LABELS[segment];
  return segment
    .split("-")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}

export function Breadcrumbs() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Never show on the home page — the header already anchors "you are home".
  if (pathname === "/" || pathname === "") return null;

  const segments = pathname.split("/").filter(Boolean);

  // Build cumulative hrefs: /a, /a/b, /a/b/c
  const crumbs = segments.map((seg, i) => ({
    href: "/" + segments.slice(0, i + 1).join("/"),
    label: labelForSegment(seg),
    isLast: i === segments.length - 1,
  }));

  // BreadcrumbList JSON-LD for search engines. Emitting it here means every
  // non-home route ships a valid breadcrumb schema without each route file
  // having to opt in individually.
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    inLanguage: "en-US",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: `${SITE_ORIGIN}/`,
      },
      ...crumbs.map((c, i) => ({
        "@type": "ListItem",
        position: i + 2,
        name: c.label,
        item: `${SITE_ORIGIN}${c.href}`,
      })),
    ],
  };

  return (
    <nav
      aria-label="Breadcrumb"
      className="border-b border-border/60 bg-[var(--color-parchment)]/60"
    >

      <ol className="mx-auto flex max-w-7xl flex-wrap items-center gap-1.5 px-4 sm:px-6 py-2 text-xs text-muted-foreground">
        <li className="flex items-center">
          <Link
            to="/"
            className="inline-flex items-center gap-1 rounded px-1 py-0.5 hover:text-foreground hover:underline"
          >
            <Home className="h-3 w-3" aria-hidden="true" />
            <span className="sr-only">Home</span>
          </Link>
        </li>
        {crumbs.map((c) => (
          <li key={c.href} className="flex items-center gap-1.5">
            <ChevronRight
              className="h-3 w-3 text-muted-foreground/60"
              aria-hidden="true"
            />
            {c.isLast ? (
              <span
                aria-current="page"
                className="font-medium text-foreground"
              >
                {c.label}
              </span>
            ) : (
              // Non-terminal crumbs use a plain anchor so we don't have to
              // enumerate typed routes for every possible parent path.
              <a
                href={c.href}
                className="rounded px-1 py-0.5 hover:text-foreground hover:underline"
              >
                {c.label}
              </a>
            )}
          </li>
        ))}
      </ol>
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
    </nav>
  );

}
